import type { AgentDefinitionResponse } from '@erp/rpc';

/**
 * A background agent's procedures are configured under a single top-level
 * `config.procedures = { enabled, sopIds }`:
 *  - `enabled` grants the operator the `list_procedures` + `get_procedure` tools
 *    (and gates the deterministic `run_procedure` replay).
 *  - `sopIds` is the allowlist — the only procedures those tools can see.
 *
 * It is a top-level key (not nested under `webBrowser`) because the Tools tab
 * rebuilds `config.webBrowser` from a fixed field set on every save, so anything
 * stashed inside it would be dropped. A top-level key survives saves from any tab.
 *
 * Legacy fallback: older agents kept the allowlist as `config.webBrowser.sopIds`
 * (pinned procedures) or `config.browserSops` (attach-for-reference). We read those
 * when `config.procedures` is absent so un-migrated agents keep working; the next
 * save from the Procedures tab writes the unified `config.procedures`.
 * ponytail: drop the legacy read once every agent config carries `config.procedures`.
 */
type Config = AgentDefinitionResponse['config'] | undefined;

export type ProcedureConfig = { enabled: boolean; sopIds: string[] };

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string');
}

function legacySopIds(config: Config): string[] {
  const webBrowser =
    config && typeof config.webBrowser === 'object' && config.webBrowser
      ? (config.webBrowser as Record<string, unknown>)
      : undefined;
  const pinned = toStringArray(webBrowser?.sopIds);
  return pinned.length > 0 ? pinned : toStringArray(config?.browserSops);
}

export function getProcedureConfig(config: Config): ProcedureConfig {
  const raw =
    config && typeof config.procedures === 'object' && config.procedures
      ? (config.procedures as Record<string, unknown>)
      : undefined;
  if (raw) {
    return { enabled: raw.enabled === true, sopIds: toStringArray(raw.sopIds) };
  }
  // Un-migrated agent: a populated legacy allowlist reads as enabled.
  const legacy = legacySopIds(config);
  return { enabled: legacy.length > 0, sopIds: legacy };
}

function writeProcedureConfig(
  config: Config,
  next: ProcedureConfig,
): AgentDefinitionResponse['config'] {
  return { ...config, procedures: next };
}

export function setProceduresEnabled(
  config: Config,
  enabled: boolean,
): AgentDefinitionResponse['config'] {
  return writeProcedureConfig(config, {
    ...getProcedureConfig(config),
    enabled,
  });
}

/** Toggle a SOP id in/out of the allowlist (the per-procedure checkbox). */
export function toggleAllowedSopId(
  config: Config,
  sopId: string,
): AgentDefinitionResponse['config'] {
  const current = getProcedureConfig(config);
  const sopIds = current.sopIds.includes(sopId)
    ? current.sopIds.filter((id) => id !== sopId)
    : [...current.sopIds, sopId];
  return writeProcedureConfig(config, { ...current, sopIds });
}

/** Idempotently add a SOP id to the allowlist AND enable procedures — used when a
 * procedure is taught from an agent's Procedures tab, so it's usable right away. */
export function addAllowedSopId(
  config: Config,
  sopId: string,
): AgentDefinitionResponse['config'] {
  const current = getProcedureConfig(config);
  const sopIds = current.sopIds.includes(sopId)
    ? current.sopIds
    : [...current.sopIds, sopId];
  return writeProcedureConfig(config, { enabled: true, sopIds });
}

/**
 * Tools whose operator can use procedures. Both run on the same computer-use operator, so both get
 * the read-and-follow pair (`list_procedures` + `get_procedure`).
 *
 * They differ in one thing: only the browser gets the deterministic `run_procedure` replay. A
 * procedure's captured steps are Playwright code bound to a live DOM, and a remote desktop is a
 * streamed video canvas whose page is the client chrome AROUND the app — so the ai-service withholds
 * that tool on a visual-only surface (`strands_driver`) and the operator follows the procedure with
 * computer-use actions instead.
 */
const PROCEDURE_CAPABLE_TOOL_TYPES = [
  'web_browser_execute',
  'remote_desktop_execute',
];

/** Does this definition have a tool that can use procedures? Gates the Procedures tab and the
 * workflow node's per-node procedure picker, so neither offers a config nothing would read. */
export function hasProcedureCapableTool(
  tools: { toolType: string }[] | undefined,
): boolean {
  return (tools ?? []).some((tool) =>
    PROCEDURE_CAPABLE_TOOL_TYPES.includes(tool.toolType),
  );
}
